<template>
  <div class="page-wrapper">
    <div class="container-narrow">

      <!-- Page Header - Using PageHero component -->
      <PageHero title="Contact Us" />

      <!-- Success: message sent (with or without subscribe) -->
      <div v-if="submitted && !subscribedOnly" class="content-section centered">
        <h2 class="text-success">Message Sent!</h2>
        <p>Thank you for contacting us. We've received your message and will respond as soon as possible.</p>
        <p v-if="formData.subscribe">You've also been added to our email newsletter.</p>
      </div>

      <!-- Success: subscribe only -->
      <div v-else-if="submitted && subscribedOnly" class="content-section centered">
        <h2 class="text-success">You're Subscribed!</h2>
        <p>{{ subscribeConfirmText }}</p>
      </div>

      <!-- Error Message -->
      <div v-else-if="errorMessage" class="content-section centered">
        <h2 class="text-error">Error Sending Message</h2>
        <p>{{ errorMessage }}</p>
        <p>You can always email us directly at <a href="mailto:comments@organlive.com" class="link-primary">comments@organlive.com</a></p>
        <button @click="resetForm" class="btn btn-primary mt-2">Try Again</button>
      </div>

      <!-- Contact Form -->
      <div v-else class="content-section">
        <p class="intro">
          We'd love to hear from you! Whether you have questions, feedback, or suggestions, please use the form below to get in touch with us.
        </p>

        <form @submit.prevent="submitForm" class="contact-form">
          <div class="form-group">
            <label for="name">Name</label>
            <input
              type="text"
              id="name"
              v-model="formData.name"
              :required="!subscribeOnly"
              placeholder="Your name"
              :disabled="isSubmitting"
            />
          </div>

          <div class="form-group">
            <label for="email">Email</label>
            <input
              type="email"
              id="email"
              v-model="formData.email"
              required
              placeholder="your.email@example.com"
              :disabled="isSubmitting"
            />
          </div>
          <!-- Newsletter subscribe checkbox -->
          <div class="form-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="formData.subscribe"
                :disabled="isSubmitting"
              />
              <span>Subscribe me to the Organ Media Foundation email newsletter</span>
            </label>
          </div>
          <div class="form-group">
            <label for="message">
              Message
              <span v-if="subscribeOnly" class="label-optional">(optional)</span>
            </label>
            <textarea
              id="message"
              v-model="formData.message"
              :required="!subscribeOnly"
              placeholder="Your message..."
              rows="8"
              :disabled="isSubmitting"
            ></textarea>
          </div>

          <button type="submit" class="btn btn-primary btn-large"
                  :disabled="isSubmitting || !canSubmit">
            {{ isSubmitting ? 'Sending...' : submitLabel }}
          </button>
        </form>
      </div>

    </div>
  </div>
</template>

<script>
import PageHero from '@/components/PageHero.vue'

export default {
  name: 'ContactPage',
  components: {
    PageHero
  },
  data () {
    return {
      formData: {
        name: '',
        email: '',
        message: '',
        subscribe: false
      },
      isSubmitting: false,
      submitted: false,
      subscribedOnly: false,
      errorMessage: ''
    }
  },
  computed: {
    // True when checkbox is checked but message is empty
    subscribeOnly () {
      return this.formData.subscribe && !this.formData.message.trim()
    },
    // Must have email + at least a message or the subscribe checkbox
    canSubmit () {
      return this.formData.email.trim() &&
             (this.formData.message.trim() || this.formData.subscribe)
    },
    submitLabel () {
      if (this.subscribeOnly) return 'Subscribe'
      if (this.formData.subscribe) return 'Send & Subscribe'
      return 'Send Message'
    },
    subscribeConfirmText () {
      const who = this.formData.name
        ? this.formData.name + ", you've"
        : "You've"
      return who + " been added to the Organ Media Foundation email newsletter. We'll be in touch!"
    }
  },
  mounted () {
    document.title = 'Organlive.com - Contact Us'
  },
  methods: {
    async submitForm () {
      if (!this.canSubmit) return
      this.isSubmitting = true
      this.errorMessage = ''

      try {
        const formData = new URLSearchParams()
        formData.append('name', this.formData.name)
        formData.append('email', this.formData.email)
        formData.append('message', this.formData.message)
        formData.append('subscribe', this.formData.subscribe ? '1' : '0')

        const response = await fetch('https://api.organlive.com/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formData
        })

        const data = await response.json()

        if (response.ok && data.message === 'success') {
          this.subscribedOnly = this.subscribeOnly
          this.submitted = true
          this.formData = { name: '', email: '', message: '', subscribe: false }
        } else {
          this.errorMessage = data.error || 'An unknown error occurred.'
        }
      } catch (error) {
        console.error('Form submission error:', error)
        this.errorMessage = 'An unknown error occurred.'
      } finally {
        this.isSubmitting = false
      }
    },
    resetForm () {
      this.errorMessage = ''
      this.submitted = false
      this.subscribedOnly = false
      this.formData = { name: '', email: '', message: '', subscribe: false }
    }
  }
}
</script>

<style scoped>
/* Only contact-specific styles remain - everything else is global */

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 2rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  color: var(--color-white);
  font-size: 1rem;
  font-weight: 600;
}

.form-group input,
.form-group textarea {
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(102, 178, 255, 0.3);
  border-radius: 6px;
  padding: 0.75rem;
  color: var(--color-white);
  font-size: 1rem;
  font-family: inherit;
  transition: border-color var(--transition-default), background-color var(--transition-default);
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--color-primary);
  background: rgba(255, 255, 255, 0.15);
}

.form-group input:disabled,
.form-group textarea:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.form-group textarea {
  resize: vertical;
  min-height: 150px;
}

.label-optional {
  font-weight: normal;
  font-size: 0.85em;
  opacity: 0.65;
  margin-left: 6px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  font-weight: normal;
}

.checkbox-label input[type="checkbox"] {
  margin-top: 3px;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--color-primary);
}

.checkbox-label span {
  color: var(--color-white);
  font-size: 1rem;
  line-height: 1.4;
}

/* Responsive adjustments specific to this form */
@media (max-width: 768px) {
  .form-group input,
  .form-group textarea {
    font-size: 0.95rem;
  }
}
</style>
